import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { BlockTypeEnum } from '@core/enums';
import { Block } from '@core/models/block/block.model';
import { GlobalService, MediaQuery } from '@core/services/common/global.service';
import { getStyles, mergeAllStyles } from '@shared/helpers/block-helpers';
import { camelCase } from 'lodash';
import { Subscription } from 'rxjs';

@Component({
  selector: '[app-block-container]',
  templateUrl: './block-container.component.html',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class BlockContainerComponent implements OnInit, OnDestroy {
  @Input() children: Block[];
  @Input() disableTabIndex: boolean;
  private mediaQuery: MediaQuery;
  private mediaQuerySubscription: Subscription;

  constructor(private globalService: GlobalService) {
  }

  getChildStyle(item: Block, forExternal = false) {
    const {
      width,
      marginLeft,
      marginRight,
      marginTop,
      marginBottom,
    } = this.getItemStyleProps(item, [
      'width',
      'marginLeft',
      'marginRight',
      'marginTop',
      'marginBottom',
    ]);
    const styleObj: any = {};
    if (marginLeft) {
      styleObj.marginLeft = marginLeft;
    }
    if (marginRight) {
      styleObj.marginRight = marginRight;
    }
    if (marginTop) {
      styleObj.marginTop = marginTop;
    }
    if (marginBottom) {
      styleObj.marginBottom = marginBottom;
    }
    if (!width && !forExternal) {
      styleObj.width = '100%';
      return styleObj;
    }
    if (!width?.includes('%')) {
      styleObj.width = width;
      return styleObj;
    }

    styleObj.width = this.getCalculatedWidth(width, marginLeft, marginRight);
    return styleObj;
  }

  getChildStyleWidth(child: Block): string {
    const {width} = this.getItemStyleProps(child, ['width']);
    if (width?.includes('%')) {
      return width;
    }
    return '100%';
  }

  getSpecialBlockClassIfNecessary(item: Block) {
    const classObj: any = {};
    if (item.specialBlock) {
      if (item.specialBlock.type === BlockTypeEnum.SLIDER_TYPE) {
        const {width, height} = this.getItemStyleProps(item, ['width', 'height']);
        if (!width) {
          classObj['slider-full-width'] = true;
        }
        if (!height) {
          classObj['slider-full-height'] = true;
        }
      }
    }
    return classObj;
  }

  getChildStyleFixedWidthAndMargin(item: Block, styles: any) {
    styles = {...styles};
    if (item?.link?.hasExternalLink()) {
      if (styles.width?.includes('%')) {
        styles.width = '100%';
      }
      return styles;
    }

    return this.getChildStyle(item);
  }

  getLineClamp(item: Block) {
    if (!item.styles) {
      return '';
    }
    const querySnakeCase = camelCase(this.mediaQuery.query);
    const {basic} = getStyles(item.styles, querySnakeCase);
    return basic['-webkitLineClamp'];
  }

  ngOnInit(): void {
    this.mediaQuerySubscription = this.globalService.mediaQuery.subscribe(
      (mediaQuery) => (this.mediaQuery = mediaQuery),
    );
    this.children.forEach(e => {
      if (e?.children.length > 0 && e.children.some(block => block.node === 20)) {
        this.globalService.styleIfameParent = e.styles;
      }
    });
  }

  ngOnDestroy() {
    this.mediaQuerySubscription.unsubscribe();
  }

  private getItemStyleProps(item: Block, props: string[]): Record<string, string> {
    if (!item.styles) {
      return {};
    }

    const querySnakeCase = camelCase(this.mediaQuery.query);
    const {basic} = mergeAllStyles(item.styles, querySnakeCase);
    const styles = {};
    props.forEach(prop => {
      styles[prop] = basic[prop];
    });

    return styles;
  }

  private getCalculatedWidth(width: string, marginLeft: any, marginRight: any): string {
    if (marginRight && marginLeft) {
      return `calc(${width} - (${marginLeft} + ${marginRight}))`;
    }

    if (!marginLeft && !marginRight) {
      return width;
    }

    if (marginLeft) {
      return `calc(${width} - ${marginLeft})`;
    }

    if (marginRight) {
      return `calc(${width} - ${marginRight})`;
    }
    return '';
  }
}
